import { type ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from './cn';

export interface EmptyStateProps {
  /** Optional lucide icon rendered above the title (muted, in a soft circle). */
  icon?: LucideIcon;
  /** Primary line — what is empty / why. */
  title: ReactNode;
  /** Optional secondary explanation or hint. */
  description?: ReactNode;
  /** Optional action(s) — e.g. a "필터 초기화" or "새로 만들기" button. */
  action?: ReactNode;
  /**
   * Layout size. 'inline' for inside a table/card body (compact),
   * 'block' for a full empty page region. Default 'block'.
   */
  size?: 'inline' | 'block';
  className?: string;
}

/**
 * EmptyState — 빈 목록·검색 결과 없음·필터 0건 등 공용 빈 상태 패턴 (DESIGN-SPEC §10).
 * 뉴트럴 톤·중앙 정렬·hairline 없음(컨테이너가 이미 보더를 가짐). 강조는 action 버튼에만.
 */
export const EmptyState = ({
  icon: Icon,
  title,
  description,
  action,
  size = 'block',
  className,
}: EmptyStateProps) => (
  <div
    className={cn(
      'flex flex-col items-center justify-center text-center',
      size === 'block' ? 'gap-3 px-6 py-16' : 'gap-2 px-4 py-10',
      className,
    )}
    role="status"
  >
    {Icon ? (
      <span
        aria-hidden="true"
        className={cn(
          'flex items-center justify-center rounded-full',
          size === 'block' ? 'h-12 w-12' : 'h-9 w-9',
        )}
        style={{ background: 'var(--surface-1)', color: 'var(--content-muted)' }}
      >
        <Icon size={size === 'block' ? 22 : 16} />
      </span>
    ) : null}
    <p
      className={cn('font-medium', size === 'block' ? 'text-[14px]' : 'text-[13px]')}
      style={{ color: 'var(--content-default)' }}
    >
      {title}
    </p>
    {description ? (
      <p
        className="max-w-[420px] text-[12px]"
        style={{ color: 'var(--content-muted)', lineHeight: 'var(--leading-body)' }}
      >
        {description}
      </p>
    ) : null}
    {action ? <div className="mt-1 flex items-center gap-2">{action}</div> : null}
  </div>
);
